import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { snakify } from 'helpers/syntaxHelper';
import {
    DEFAULT_GRID_PROPS,
    getDefaultGridProps,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { useQueryClient } from '@tanstack/react-query';
import { useGetEnvelopePayComponentHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/PayComponentHypothesis/helpers/hooks/payComponentHypothesis';
import { ApplyChangesButton } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ApplyChangesButton';
import {
    TRACKABLE_EVENTS,
    TRACKABLE_EVENTS_METADATA,
    trackEventPRH,
} from 'helpers/trackerHelpers';
import { getPayComponentsColDefs } from './helpers/colDefinitions';
import {
    useGetHrSimulationPayComponentsData,
    HR_SIMULATION_PAY_COMPONENTS_DATA_API_KEY,
} from './helpers/hooks/payComponents';
import CreateEnvelopePayComponentHypothesisCellRenderer from '../Envelope/PayComponentHypothesis/CreateEnvelopePayComponentHypothesisCellRenderer';
import EditEnvelopePayComponentHypothesisCellRenderer from '../Envelope/PayComponentHypothesis/EditEnvelopePayComponentHypothesisCellRenderer';
import { useHypothesesChangesContext } from '../components/contexts/HypothesesChangesContext';
import AddEnvelopePayComponentHypothesisModal from './AddEnvelopePayComponentHypothesisModal/AddEnvelopePayComponentHypothesisModal';
import { useDeepMemo } from 'hooks/helpers';
import { PayComponentsDataGridContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/PayComponents/types';
import { GridApi, GridReadyEvent } from 'ag-grid-community';

type Props = {
    hrSimulation: HrSimulation;
};

const PayComponentsDataGrid = ({ hrSimulation }: Props): React.ReactElement => {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { hasChanges, setHasChanges } = useHypothesesChangesContext();

    const [showAddEnvelopeModal, setShowAddEnvelopeModal] = useState(false);

    const { title, id } = hrSimulation;
    const {
        data: envelopePayComponentHypothesesResponse,
        isLoading: areHypothesesLoading,
        isFetching: areHypothesesFetching,
    } = useGetEnvelopePayComponentHypotheses(id);

    const envelopePayComponentHypotheses =
        envelopePayComponentHypothesesResponse ?? [];

    const {
        data: payComponentsResponseData,
        isLoading: isPayComponentsLoading,
        isFetching: isPayComponentsFetching,
    } = useGetHrSimulationPayComponentsData(id);

    const payComponentsData = payComponentsResponseData?.data ?? [];

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
    }, []);

    useEffect(() => {
        gridApi?.refreshCells();
    }, [envelopePayComponentHypothesesResponse, gridApi]);

    const columnDefs = useDeepMemo(
        () =>
            getPayComponentsColDefs({
                hrSimulation,
                envelopePayComponentHypotheses,
            }),
        [hrSimulation, envelopePayComponentHypotheses],
    );

    const exportFileName = t(
        'humanResources.simulation.payComponents.fileName',
        {
            title: snakify(title),
        },
    );

    const gridProps = {
        ...getDefaultGridProps(),
        components: {
            ...DEFAULT_GRID_PROPS.components,
            createEnvelopePayComponentHypothesisCellRenderer:
                CreateEnvelopePayComponentHypothesisCellRenderer,
            editEnvelopePayComponentHypothesisCellRenderer:
                EditEnvelopePayComponentHypothesisCellRenderer,
        },
        context: {
            envelopePayComponentHypotheses,
        } as PayComponentsDataGridContext,
    };

    const reloadPayComponentsData = () => {
        queryClient.invalidateQueries({
            queryKey: [HR_SIMULATION_PAY_COMPONENTS_DATA_API_KEY, id],
        });
        setHasChanges(false);
        trackEventPRH(
            TRACKABLE_EVENTS.human_resources.simulation.payComponents
                .dataRefresh,
        );
    };

    const headerActions = (
        <ApplyChangesButton
            isLoading={isPayComponentsFetching}
            isDisabled={hasChanges === false}
            onClick={reloadPayComponentsData}
            title={t('humanResources.simulation.actualizeTable')}
        />
    );

    const footerActions = (
        <Button
            sx={{ color: 'primary' }}
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setShowAddEnvelopeModal(true)}
        >
            {t('humanResources.simulation.addEnvelope.button')}
        </Button>
    );

    const isLoading = isPayComponentsLoading || areHypothesesLoading;
    const isFetching = isPayComponentsFetching || areHypothesesFetching;

    return (
        <>
            <StyHrDataGrid
                title={t('humanResources.simulation.payComponents.title')}
                rowData={payComponentsData}
                columnDefs={columnDefs}
                exportFileName={exportFileName}
                isLoading={isLoading}
                isFetching={isFetching}
                trackEventMetadata={{
                    gridName:
                        TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                            .payComponentGrid,
                }}
                cardHeaderActions={headerActions}
                cardFooterActions={footerActions}
                gridProps={gridProps}
                onGridReady={onGridReady}
            />
            <AddEnvelopePayComponentHypothesisModal
                open={showAddEnvelopeModal}
                onClose={() => setShowAddEnvelopeModal(false)}
                hrSimulation={hrSimulation}
                envelopePayComponentHypotheses={envelopePayComponentHypotheses}
            />
        </>
    );
};

export default memo(PayComponentsDataGrid);
